import React, { useState } from 'react';
import { Card, Button, Collapse } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

function SimpleBlock({ title, s }) {
  if (!s) return null;
  const num = (x) => (typeof x === 'number' ? x.toLocaleString() : x);
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 200 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div>mean: {num(s.mean)}</div>
      <div>min / max: {num(s.min)} / {num(s.max)}</div>
      <div>n: {s.n}</div>
    </div>
  );
}

function DetailedBlock({ title, s }) {
  if (!s) return null;
  const num = (x) => (typeof x === 'number' ? x.toLocaleString() : x);
  const range = (a) => (Array.isArray(a) ? `${num(a[0])} .. ${num(a[1])}` : '-');
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 260 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div>mean: {num(s.mean)}</div>
      <div>median: {num(s.median)}</div>
      <div>std: {num(s.std)}</div>
      <div>min / max: {num(s.min)} / {num(s.max)}</div>
      <div>q05 / q95: {num(s.q05)} / {num(s.q95)}</div>
      <div>q25 / q75: {num(s.q25)} / {num(s.q75)}</div>
      <div>95% CI: {range(s.ci95)}</div>
      <div>n: {s.n}</div>
    </div>
  );
}

function RiskAnalysisBlock({ title, risk }) {
  if (!risk || risk.negative_count === 0) return null;
  const pct = (v) => `${(100 * v).toFixed(1)}%`;
  const num = (x) => (typeof x === 'number' ? x.toLocaleString() : x);
  
  return (
    <div style={{ border: '1px solid #ff4d4f', borderRadius: 8, padding: 12, minWidth: 280, backgroundColor: '#fff2f0' }}>
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#ff4d4f' }}>{title} Risk Analysis</div>
      <div>Negative cases: {risk.negative_count} ({pct(risk.negative_rate)})</div>
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.9em', color: '#666' }}>Severity breakdown:</div>
        <div>• Severe (&lt;-50%): {pct(risk.severe_negative_rate)}</div>
        <div>• Moderate (-10% to -50%): {pct(risk.moderate_negative_rate)}</div>
        <div>• Mild (0 to -10%): {pct(risk.mild_negative_rate)}</div>
      </div>
      <div style={{ marginTop: 8 }}>
        <div>Avg negative: {num(risk.avg_negative_amount)}</div>
        <div>Worst case: {num(risk.worst_case)}</div>
      </div>
    </div>
  );
}

export default function MonteCarloResults({ summary }) {
  if (!summary) return null;
  const pct = (v) => `${(100 * v).toFixed(1)}%`;
  const apr = summary.apr || {};
  const minBalances = summary.min_balances || {};
  const treasuryRefills = summary.treasury_refills;
  const refillCount = summary.refill_count;
  const riskAnalysis = summary.risk_analysis || {};
  const treasury = summary.treasury;
  const jackpots = summary.jackpots;
  const referrals = summary.referrals;
  
  return (
    <div>
      {/* Quick Summary */}
      <div style={{ marginBottom: 16 }}>
        <h4>Quick Summary</h4>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <SimpleBlock title="TOTAL" s={summary.total} />
          <SimpleBlock title="RISKY" s={summary.risky} />
          <SimpleBlock title="MEDIUM" s={summary.medium} />
          <SimpleBlock title="SAFE" s={summary.safe} />
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 200 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Events</div>
            <div>Bankruptcy rate: {pct(summary.bankruptcy_rate || 0)}</div>
            <div>Protection triggered: {pct(summary.protection_trigger_rate || 0)}</div>
          </div>
          {treasuryRefills && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 200 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Treasury Refills</div>
              <div>Total refilled: {treasuryRefills.mean?.toLocaleString() || 0}</div>
              <div>Refill events: {refillCount?.mean?.toFixed(1) || 0}</div>
            </div>
          )}
          {(treasury || jackpots || referrals) && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 280 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Other Balances</div>
              {treasury && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Treasury:</strong> {treasury.mean?.toLocaleString() || 0}
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({treasury.min?.toLocaleString() || 0} - {treasury.max?.toLocaleString() || 0})
                  </span>
                </div>
              )}
              {jackpots && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Jackpots:</strong> {jackpots.mean?.toLocaleString() || 0}
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({jackpots.min?.toLocaleString() || 0} - {jackpots.max?.toLocaleString() || 0})
                  </span>
                </div>
              )}
              {referrals && (
                <div>
                  <strong>Referrals:</strong> {referrals.mean?.toLocaleString() || 0}
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({referrals.min?.toLocaleString() || 0} - {referrals.max?.toLocaleString() || 0})
                  </span>
                </div>
              )}
            </div>
          )}
          {(apr.risky || apr.medium || apr.safe) && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, minWidth: 280 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>APR (%)</div>
              {apr.risky && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Risky:</strong> {apr.risky.mean?.toFixed(1) || 0}% 
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({apr.risky.min?.toFixed(1) || 0}% - {apr.risky.max?.toFixed(1) || 0}%)
                  </span>
                </div>
              )}
              {apr.medium && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Medium:</strong> {apr.medium.mean?.toFixed(1) || 0}% 
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({apr.medium.min?.toFixed(1) || 0}% - {apr.medium.max?.toFixed(1) || 0}%)
                  </span>
                </div>
              )}
              {apr.safe && (
                <div>
                  <strong>Safe:</strong> {apr.safe.mean?.toFixed(1) || 0}% 
                  <span style={{ fontSize: '0.8em', color: '#666' }}>
                    ({apr.safe.min?.toFixed(1) || 0}% - {apr.safe.max?.toFixed(1) || 0}%)
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detailed Statistics */}
      <Collapse ghost>
        <Panel header="Detailed Statistics" key="1">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <DetailedBlock title="TOTAL" s={summary.total} />
            <DetailedBlock title="RISKY" s={summary.risky} />
            <DetailedBlock title="MEDIUM" s={summary.medium} />
            <DetailedBlock title="SAFE" s={summary.safe} />
            <DetailedBlock title="WIN %" s={summary.win_percentage} />
          </div>
          
          {(apr.risky || apr.medium || apr.safe) && (
            <div style={{ marginTop: 16 }}>
              <h5>APR Statistics (%)</h5>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {apr.risky && <DetailedBlock title="APR RISKY" s={apr.risky} />}
                {apr.medium && <DetailedBlock title="APR MEDIUM" s={apr.medium} />}
                {apr.safe && <DetailedBlock title="APR SAFE" s={apr.safe} />}
              </div>
            </div>
          )}
          
          {(minBalances.risky || minBalances.medium || minBalances.safe) && (
            <div style={{ marginTop: 16 }}>
              <h5>Minimum Pool Balances (Risk Analysis)</h5>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {minBalances.risky && <DetailedBlock title="MIN RISKY" s={minBalances.risky} />}
                {minBalances.medium && <DetailedBlock title="MIN MEDIUM" s={minBalances.medium} />}
                {minBalances.safe && <DetailedBlock title="MIN SAFE" s={minBalances.safe} />}
              </div>
            </div>
          )}
          
          {(treasuryRefills || refillCount) && (
            <div style={{ marginTop: 16 }}>
              <h5>Treasury Refill Statistics</h5>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {treasuryRefills && <DetailedBlock title="TOTAL REFILLED" s={treasuryRefills} />}
                {refillCount && <DetailedBlock title="REFILL COUNT" s={refillCount} />}
              </div>
            </div>
          )}
          
          {(treasury || jackpots || referrals) && (
            <div style={{ marginTop: 16 }}>
              <h5>Other Balance Statistics</h5>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {treasury && <DetailedBlock title="TREASURY" s={treasury} />}
                {jackpots && <DetailedBlock title="JACKPOTS" s={jackpots} />}
                {referrals && <DetailedBlock title="REFERRALS" s={referrals} />}
              </div>
            </div>
          )}
          
          {(riskAnalysis.safe?.negative_count > 0 || riskAnalysis.medium?.negative_count > 0 || riskAnalysis.risky?.negative_count > 0) && (
            <div style={{ marginTop: 16 }}>
              <h5>Detailed Risk Analysis (Negative Balance Scenarios)</h5>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {riskAnalysis.safe?.negative_count > 0 && <RiskAnalysisBlock title="SAFE" risk={riskAnalysis.safe} />}
                {riskAnalysis.medium?.negative_count > 0 && <RiskAnalysisBlock title="MEDIUM" risk={riskAnalysis.medium} />}
                {riskAnalysis.risky?.negative_count > 0 && <RiskAnalysisBlock title="RISKY" risk={riskAnalysis.risky} />}
              </div>
            </div>
          )}
        </Panel>
      </Collapse>
    </div>
  );
}


